import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera } from "lucide-react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import SubmitPhotoForm from "@/components/SubmitPhotoForm";

export default function PhotoOfTheMonth() {
  const { id } = useParams();
  const [potm, setPotm] = useState(null);
  const [archive, setArchive] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const url = id ? `/potm/${id}` : "/potm/current";
    api.get(url).then(({ data }) => { if (data) setPotm(data); else setMissing(true); }).catch(() => setMissing(true));
    api.get("/potm").then(({ data }) => setArchive(data || [])).catch(() => {});
  }, [id]);

  if (missing)
    return (
      <div data-testid="potm-empty" className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <p className="font-display text-4xl font-bold mb-4">No feature yet</p>
          <p className="text-ink/60 mb-6">The Canary Photo of the Month is coming soon.</p>
          <Link to="/" className="label link-underline pb-1">Back home</Link>
        </div>
      </div>
    );
  if (!potm) return <div className="min-h-screen grid place-items-center font-sans text-ink/50">Loading…</div>;

  const past = archive.filter((a) => a.id !== potm.id);

  return (
    <div data-testid="potm-page">
      <Seo title="Canary Photo of the Month" description="Each month we elevate a photo from our community and sit down with the person behind the lens." />
      {/* Hero */}
      <section className="bg-ink text-white grain relative overflow-hidden pt-[110px] pb-16">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="label text-canary mb-6 flex items-center gap-2"><Camera size={15} /> Canary Photo of the Month · {potm.month}</p>
          <RevealLines as="h1"
            className="font-display font-black text-[10vw] lg:text-[6vw] leading-[0.88] tracking-tight max-w-5xl"
            lines={potm.title.split(" ").reduce((acc, w, i) => { const idx = Math.floor(i / 3); acc[idx] = (acc[idx] ? acc[idx] + " " : "") + w; return acc; }, [])}
          />
          <FadeUp delay={0.5}><p className="mt-6 label text-white/50">{potm.photographer}{potm.location ? ` · ${potm.location}` : ""}</p></FadeUp>
        </div>
      </section>

      {/* Photo */}
      <div className="hover-img max-w-[1400px] mx-auto px-5 sm:px-8 -mt-2">
        <div className="aspect-[16/10] bg-paper">
          <img src={potm.photo} alt={potm.title} className="h-full w-full object-cover" data-testid="potm-photo" />
        </div>
      </div>

      {/* Intro + interview */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        {potm.intro && <p className="font-display text-2xl sm:text-3xl leading-snug mb-12">{potm.intro}</p>}

        {potm.interview?.length > 0 && (
          <div data-testid="potm-interview" className="space-y-10 border-t border-ink/15 pt-12">
            <p className="label text-ink/50">The story behind it — in their words</p>
            {potm.interview.map((qa, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <p className="font-display text-xl sm:text-2xl font-bold text-ink mb-3">{qa.question}</p>
                <p className="text-lg text-ink/75 leading-[1.8]">{qa.answer}</p>
              </FadeUp>
            ))}
          </div>
        )}

        {/* Submit your photo */}
        <SubmitPhotoForm />
      </section>

      {/* Archive */}
      {past.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-24">
          <FadeUp><h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight border-t border-ink/15 pt-12 mb-10">Past features</h2></FadeUp>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {past.map((a) => (
              <StaggerItem key={a.id}>
                <Link to={`/photo-of-the-month/${a.id}`} data-testid={`potm-archive-${a.id}`} className="group block">
                  <div className="hover-img aspect-[4/3] bg-paper mb-5"><img src={a.photo} alt={a.title} className="h-full w-full object-cover" /></div>
                  <p className="label text-ink/50 mb-2">{a.month}</p>
                  <h3 className="font-display text-xl font-bold leading-snug group-hover:text-ink/60 transition-colors">{a.title}</h3>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </section>
      )}
    </div>
  );
}
